import { useEffect } from 'react'

// Stack of currently-open find-scope layers (LIFO). The last entry is the
// topmost layer whose content should be searchable by the browser's native
// Ctrl+F. Everything "below" it is hidden via `visibility: hidden` so the
// find-in-page traversal skips it.
const stack = []

// For a given top layer, the list of selectors to hide. We only hide known
// elements (base app + lower layers) rather than every body child, so that
// unrelated portals (snackbars, tooltips, upgrade prompts) keep working.
const HIDE_TARGETS = {
  'card-modal': ['#root'],
  'main-popup': ['#root', '[data-find-scope="card-modal"]'],
  'nested-popup': [
    '#root',
    '[data-find-scope="card-modal"]',
    '#main-popup-container',
  ],
}

const HIDDEN_SELECTOR = '[data-find-scope-hidden="1"]'

function reveal(el) {
  if (el?.dataset?.findScopeHidden === '1') {
    el.style.visibility = ''
    delete el.dataset.findScopeHidden
  }
}

function hide(el) {
  if (!el) return
  if (el.dataset.findScopeHidden !== '1') {
    el.dataset.findScopeHidden = '1'
    el.style.visibility = 'hidden'
  }
}

function applyScope() {
  if (typeof document === 'undefined' || !document.body) return

  // Always start by revealing anything previously hidden by us.
  document.querySelectorAll(HIDDEN_SELECTOR).forEach(reveal)

  const top = stack[stack.length - 1]
  if (!top) return

  const selectors = HIDE_TARGETS[top.type] || []
  selectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach(hide)
  })
}

// Hook: register an open popup/modal as the topmost find-scope layer while
// `active` is true. `type` is one of the keys in HIDE_TARGETS.
export default function useFindScopeLayer(active, type) {
  useEffect(() => {
    if (!active) return undefined

    const layer = { type }
    stack.push(layer)
    applyScope()

    return () => {
      const idx = stack.lastIndexOf(layer)
      if (idx !== -1) stack.splice(idx, 1)
      applyScope()
    }
  }, [active, type])
}
